import { useState, useRef, useEffect } from 'react';
import {
  Search,
  Bell,
  Sun,
  Moon,
  Monitor,
  ChevronDown,
  LogOut,
  User,
  Settings,
} from 'lucide-react';
import { useAuth, DEPARTMENT_CONFIG } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { cn, getInitials } from '@/lib/utils';

export function Header() {
  const { user, logout } = useAuth();
  const { theme, setTheme, resolvedTheme } = useTheme();
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isThemeMenuOpen, setIsThemeMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const themeMenuRef = useRef<HTMLDivElement>(null);

  // Close menus when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
      if (themeMenuRef.current && !themeMenuRef.current.contains(event.target as Node)) {
        setIsThemeMenuOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const departmentLabel = user
    ? DEPARTMENT_CONFIG[user.primaryDepartment].label
    : '';

  return (
    <header className="h-16 bg-white border-b border-gray dark:bg-navy dark:border-night-pond">
      <div className="h-full px-6 flex items-center justify-between">
        {/* Search */}
        <div className="flex-1 max-w-xl">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-french-gray" />
            <input
              type="text"
              placeholder="Search clients, invoices, products..."
              className="input pl-10 py-2"
            />
          </div>
        </div>

        {/* Right side actions */}
        <div className="flex items-center gap-2">
          {/* Notifications */}
          <button className="btn-ghost btn-icon relative">
            <Bell className="w-5 h-5" />
            <span className="absolute top-1 right-1 w-2 h-2 bg-rust rounded-full" />
          </button>

          {/* Theme Toggle */}
          <div className="relative" ref={themeMenuRef}>
            <button
              onClick={() => setIsThemeMenuOpen(!isThemeMenuOpen)}
              className="btn-ghost btn-icon"
            >
              {resolvedTheme === 'dark' ? (
                <Moon className="w-5 h-5" />
              ) : (
                <Sun className="w-5 h-5" />
              )}
            </button>

            {isThemeMenuOpen && (
              <div className="absolute right-0 mt-2 w-36 bg-white rounded-lg shadow-lg border border-gray dark:bg-navy dark:border-night-pond z-50 animate-fade-in">
                <div className="py-1">
                  <button
                    onClick={() => {
                      setTheme('light');
                      setIsThemeMenuOpen(false);
                    }}
                    className={cn(
                      'w-full flex items-center gap-2 px-4 py-2 text-sm hover:bg-pale-gray-2 dark:hover:bg-puddle/30',
                      theme === 'light' && 'text-pond dark:text-pond-light font-medium'
                    )}
                  >
                    <Sun className="w-4 h-4" />
                    Light
                  </button>
                  <button
                    onClick={() => {
                      setTheme('dark');
                      setIsThemeMenuOpen(false);
                    }}
                    className={cn(
                      'w-full flex items-center gap-2 px-4 py-2 text-sm hover:bg-pale-gray-2 dark:hover:bg-puddle/30',
                      theme === 'dark' && 'text-pond dark:text-pond-light font-medium'
                    )}
                  >
                    <Moon className="w-4 h-4" />
                    Dark
                  </button>
                  <button
                    onClick={() => {
                      setTheme('system');
                      setIsThemeMenuOpen(false);
                    }}
                    className={cn(
                      'w-full flex items-center gap-2 px-4 py-2 text-sm hover:bg-pale-gray-2 dark:hover:bg-puddle/30',
                      theme === 'system' && 'text-pond dark:text-pond-light font-medium'
                    )}
                  >
                    <Monitor className="w-4 h-4" />
                    System
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* User Menu */}
          <div className="relative ml-2" ref={userMenuRef}>
            <button
              onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
              className="flex items-center gap-3 hover:bg-pale-gray-2 dark:hover:bg-puddle/30 rounded-lg px-3 py-2 transition-colors"
            >
              {/* Avatar */}
              {user?.avatarUrl ? (
                <img
                  src={user.avatarUrl}
                  alt={user.displayName}
                  className="w-8 h-8 rounded-full"
                />
              ) : (
                <div className="w-8 h-8 rounded-full bg-pond flex items-center justify-center">
                  <span className="text-white text-sm font-medium">
                    {user ? getInitials(user.displayName) : '?'}
                  </span>
                </div>
              )}
              <div className="hidden md:block text-left">
                <p className="text-sm font-medium text-slate dark:text-mist">
                  {user?.displayName}
                </p>
                <p className="text-xs text-chalkboard dark:text-rain-cloud">
                  {departmentLabel}
                </p>
              </div>
              <ChevronDown className="w-4 h-4 text-chalkboard dark:text-rain-cloud" />
            </button>

            {isUserMenuOpen && (
              <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray dark:bg-navy dark:border-night-pond z-50 animate-fade-in">
                {/* User info */}
                <div className="px-4 py-3 border-b border-gray dark:border-night-pond">
                  <p className="text-sm font-medium text-slate dark:text-mist">
                    {user?.displayName}
                  </p>
                  <p className="text-xs text-chalkboard dark:text-rain-cloud">
                    {user?.email}
                  </p>
                </div>

                {/* Menu items */}
                <div className="py-1">
                  <button className="w-full flex items-center gap-2 px-4 py-2 text-sm text-slate dark:text-mist hover:bg-pale-gray-2 dark:hover:bg-puddle/30">
                    <User className="w-4 h-4" />
                    Profile
                  </button>
                  <button className="w-full flex items-center gap-2 px-4 py-2 text-sm text-slate dark:text-mist hover:bg-pale-gray-2 dark:hover:bg-puddle/30">
                    <Settings className="w-4 h-4" />
                    Settings
                  </button>
                </div>

                {/* Logout */}
                <div className="border-t border-gray dark:border-night-pond py-1">
                  <button
                    onClick={() => {
                      logout();
                      setIsUserMenuOpen(false);
                    }}
                    className="w-full flex items-center gap-2 px-4 py-2 text-sm text-rust dark:text-salmon hover:bg-pale-gray-2 dark:hover:bg-puddle/30"
                  >
                    <LogOut className="w-4 h-4" />
                    Sign out
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}
